import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "helpers";

import {
  deleteFleetVehicleExpenseAPI,
  getFleetVehicleExpensesAPI,
} from "services/api/fleet/vehicles/expenses/api";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { FleetVehicleExpenseRow } from "services/api/fleet/vehicles/expenses/types";

import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

import ExpenseSearchFilters from "../ExpenseSearchFilters/ExpenseSearchFilters";
import { ExpenseSearchFiltersFormValues } from "../ExpenseSearchFilters/types";
import ExpenseEditCard from "../ExpenseEditCard/ExpenseEditCard";
import ExpenseSearchResults from "../ExpenseSearchResults/ExpenseSearchResults";
import { buildVehicleDetailRoute } from "../ExpenseSearchResults/consts";
import { ExpenseSearchTabProps } from "./types";

// Filter shape se mapira u BE query params; `filters` dolaze iz parenta i
// trigeraju refetch kad se promijene.
const filtersToApiPayload = (f: ExpenseSearchFiltersFormValues) => ({
  search: f.search,
  expenseTypeId: f.expenseTypeId,
  amountMin: f.amountMin,
  amountMax: f.amountMax,
  vehicleManufacturerId: f.vehicleManufacturerId,
  vehicleModelId: f.vehicleModelId,
  vehicleTypeId: f.vehicleTypeId,
  expenseDateFrom: f.expenseDateFrom,
  expenseDateTo: f.expenseDateTo,
});

const ExpenseSearchTab: React.FC<ExpenseSearchTabProps> = ({ filters, setFilters }) => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleExpenseRow>
  >(initialDataWithPagination);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [expenseId, setExpenseId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [editExpenseId, setEditExpenseId] = useState<string | null>(null);
  const [showEditCard, setShowEditCard] = useState<boolean>(false);

  const fetchSearchResult = useCallback(
    async (pagination: Pagination) => {
      setIsLoading(true);
      try {
        const results = await getFleetVehicleExpensesAPI(
          pagination,
          filtersToApiPayload(filters)
        );
        setSearchResults(results);
      } catch {
        setSearchResults(initialDataWithPagination);
      } finally {
        setIsLoading(false);
      }
    },
    [filters]
  );

  // Initial fetch + refetch on filter change.
  useEffect(() => {
    fetchSearchResult(initialDataWithPagination.pagination);
  }, [fetchSearchResult]);

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handleVehicleClick = useCallback(
    (event: React.MouseEvent, vehicleId: string) => {
      event.stopPropagation();
      navigate(buildVehicleDetailRoute(vehicleId));
    },
    [navigate]
  );

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    id: string
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setExpenseId(id);
    setOpenDialog(true);
  };

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!expenseId) return;
    try {
      const deleted = await deleteFleetVehicleExpenseAPI(expenseId, t);
      if (deleted) {
        await fetchSearchResult(searchResults.pagination);
      }
    } finally {
      setOpenDialog(false);
      setExpenseId("");
    }
  }, [expenseId, t, fetchSearchResult, searchResults.pagination]);

  const handleAddClick = useCallback(() => {
    setEditExpenseId(null);
    setShowEditCard(true);
  }, []);

  // Troškovi nastali raspodjelom računa uređuju se kroz sam račun, da ostanu vezani uz njega.
  const handleRowClick = useCallback((id: string) => {
    const row = searchResults.data.find((expense) => expense.id === id);
    if (row?.invoiceIncoming) return;

    setEditExpenseId(id);
    setShowEditCard(true);
  }, [searchResults.data]);

  const handleEditCardClose = useCallback(() => {
    setShowEditCard(false);
    setEditExpenseId(null);
  }, []);

  const handleEditCardSuccess = useCallback(() => {
    setShowEditCard(false);
    setEditExpenseId(null);
    fetchSearchResult(searchResults.pagination);
  }, [fetchSearchResult, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setExpenseId("");
    setOpenDialog(false);
  }, []);

  return (
    <>
      <div className="px-0 py-6 mx-[-1px] xl:px-6">
        <div className="flex justify-end mb-3">
          <Button
            variant="primary"
            startIcon="/media/icons/plus.svg"
            onClick={handleAddClick}
          >
            Novi trošak
          </Button>
        </div>

        <ExpenseSearchFilters formValues={filters} setFormValues={setFilters} />
        {isLoading && searchResults.data.length === 0 ? (
          <Loader isLoading={true}>{null}</Loader>
        ) : !isLoading && searchResults.pagination.totalPages === 0 ? (
          <NoResults message="Nema troškova." />
        ) : (
          <ExpenseSearchResults
            searchResults={searchResults}
            selectedRows={selectedRows}
            isLoading={isLoading}
            onPaginationChange={handlePaginationChange}
            onSelectedChange={handleSelectedChange}
            onDeleteClick={handleDeleteClick}
            onVehicleClick={handleVehicleClick}
            onRowClick={handleRowClick}
          />
        )}
      </div>
      <ExpenseEditCard
        show={showEditCard}
        expenseId={editExpenseId}
        onSuccess={handleEditCardSuccess}
        onClose={handleEditCardClose}
      />

      <Dialog
        open={openDialog}
        title={t("admin.fleet.vehicle.expense.dialog.delete.title")}
        message={t("admin.fleet.vehicle.expense.dialog.delete.message")}
        confirmText={t("admin.fleet.vehicle.expense.dialog.delete.confirmtext")}
        cancelText={t("admin.fleet.vehicle.expense.dialog.delete.canceltext")}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

export default ExpenseSearchTab;
